import React, { useEffect, useState, useCallback } from 'react';
import { Table, Input, Breadcrumb, Modal, message, Button, DatePicker, Grid, Card, Row, Col, Statistic, Avatar, Tag, Tooltip, Dropdown, Select } from 'antd';
import { Link } from 'react-router-dom';
import {
  DeleteOutlined,
  RightOutlined,
  FileExcelOutlined,
  FilePdfOutlined,
  DownloadOutlined,
  SearchOutlined,
  CarOutlined,
  SwapRightOutlined,
  HistoryOutlined,
  UserOutlined,
  CalendarOutlined,
  BarChartOutlined,
  EnvironmentOutlined,
} from '@ant-design/icons';
import { historiqueNumerosService, citiesService } from '../../common/services';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import Can from '../../components/can';

dayjs.extend(customParseFormat);

const { RangePicker } = DatePicker;

// Fonction pour générer une couleur à partir du nom
const getAvatarColor = (name) => {
  const colors = [
    '#1890ff', '#52c41a', '#faad14', '#eb2f96', '#722ed1',
    '#13c2c2', '#fa541c', '#2f54eb', '#a0d911', '#f5222d'
  ];
  if (!name) return colors[0];
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return colors[Math.abs(hash) % colors.length];
};

// Formater le téléphone (sans le préfixe +225)
const formatPhoneNumber = (phone) => {
  if (!phone) return '-';
  let cleaned = phone.replace(/\D/g, '');
  if (cleaned.startsWith('225')) cleaned = cleaned.slice(3);
  if (cleaned.length === 10) {
    return `${cleaned.slice(0, 2)} ${cleaned.slice(2, 4)} ${cleaned.slice(4, 6)} ${cleaned.slice(6, 8)} ${cleaned.slice(8, 10)}`;
  }
  return cleaned || phone;
};

// Formater la date
const formatDate = (date) => {
  if (!date) return '-';
  const formats = [
    'YYYY-MM-DDTHH:mm:ss.SSSZ',
    'YYYY-MM-DD HH:mm:ss',
    'DD/MM/YYYY HH:mm:ss',
    'DD/MM/YYYY HH:mm',
    'DD/MM/YYYY',
  ];
  let parsed = dayjs(date);
  if (!parsed.isValid()) {
    for (const fmt of formats) {
      parsed = dayjs(date, fmt);
      if (parsed.isValid()) break;
    }
  }
  if (parsed.isValid()) {
    return parsed.format('DD/MM/YYYY HH:mm');
  }
  if (typeof date === 'string') return date;
  return '-';
};

const HistoriqueVehiculesModifies = () => {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setLoading] = useState(false);
  const [stats, setStats] = useState({
    totalChangements: 0,
    utilisateursUniques: 0,
    today: 0,
    thisMonth: 0
  });

  // filtres serveur
  const [q, setQ] = useState('');
  const [dateRange, setDateRange] = useState(null);
  const [cityFilter, setCityFilter] = useState(undefined);
  const [cities, setCities] = useState([]);

  // pagination/tri serveur
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [sort, setSort] = useState('-date');

  // Helpers responsive
  const screens = Grid.useBreakpoint();
  const isMobile = !screens.sm;
  const isTablet = screens.sm && !screens.lg;
  const controlSize = isMobile ? 'small' : 'middle';

  // Calcul des statistiques
  const calculateStats = useCallback((data) => {
    const today = dayjs().startOf('day');
    const startOfMonth = dayjs().startOf('month');

    const todayCount = data.filter(item => {
      const itemDate = dayjs(item.date);
      return itemDate.isValid() && itemDate.isAfter(today);
    }).length;

    const monthCount = data.filter(item => {
      const itemDate = dayjs(item.date);
      return itemDate.isValid() && itemDate.isAfter(startOfMonth);
    }).length;

    const uniqueUsers = new Set(data.map(item => item.phone)).size;

    setStats({
      totalChangements: data.length,
      utilisateursUniques: uniqueUsers,
      today: todayCount,
      thisMonth: monthCount
    });
  }, []);

  const fetchList = useCallback(async () => {
    try {
      setLoading(true);
      // Supprimer les espaces de la recherche pour les numéros de téléphone et immatriculations
      const cleanedQ = q ? q.replace(/\s+/g, '') : '';
      const params = {
        page,
        limit: 5000,
        sort,
        q: cleanedQ || undefined,
        start: dateRange?.[0]?.format('YYYY-MM-DD'),
        end: dateRange?.[1]?.format('YYYY-MM-DD'),
        cityCode: cityFilter || undefined,
      };
      const data = await historiqueNumerosService.historiqueVehiculesModifiesList(params);
      const allData = Array.isArray(data) ? data : (data.items || []);
      calculateStats(allData);

      // Pagination côté client
      const start = (page - 1) * limit;
      const end = start + limit;
      setItems(allData.slice(start, end));
      setTotal(allData.length);
    } catch (e) {
      console.error('Erreur chargement historique véhicules :', e);
      message.error('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  }, [page, limit, sort, q, dateRange, cityFilter, calculateStats]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  // Charger les villes (pour le filtre)
  useEffect(() => {
    (async () => {
      try {
        const res = await citiesService.getAll();
        const list = Array.isArray(res)
          ? res
          : (res?.data || res?.items || res?.cities || []);
        setCities(list.filter(c => c?.isActive !== false));
      } catch (e) {
        console.error('Erreur chargement villes:', e);
      }
    })();
  }, []);

  // Modal.confirm responsive
  const handleDelete = (row) => {
    const { _id, nom, prenoms, ancienneImmatriculation, nouvelleImmatriculation } = row;
    const fullName = `${nom || ''} ${prenoms || ''}`.trim();

    Modal.confirm({
      title: (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <Avatar
            size={40}
            style={{ backgroundColor: '#ff4d4f' }}
            icon={<DeleteOutlined />}
          />
          <span>Confirmer la suppression</span>
        </div>
      ),
      content: (
        <Card
          size="small"
          style={{ marginTop: 16, background: '#fff1f0', border: '1px solid #ffa39e' }}
        >
          <p style={{ marginBottom: 8 }}><strong>Utilisateur :</strong> {fullName}</p>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Tag color="red" style={{ margin: 0 }}>{ancienneImmatriculation}</Tag>
            <SwapRightOutlined style={{ color: '#8c8c8c' }} />
            <Tag color="green" style={{ margin: 0 }}>{nouvelleImmatriculation}</Tag>
          </div>
        </Card>
      ),
      okText: "Oui, supprimer",
      cancelText: "Annuler",
      okType: "danger",
      centered: true,
      width: isMobile ? 360 : 480,
      okButtonProps: { danger: true, size: controlSize },
      cancelButtonProps: { size: controlSize },
      onOk: async () => {
        try {
          await historiqueNumerosService.supprimerHistoriqueVehiculesModifies(_id);
          message.success("Historique supprimé avec succès !");
          fetchList();
        } catch (error) {
          console.error("Erreur lors de la suppression :", error);
          message.error("Une erreur est survenue pendant la suppression.");
        }
      }
    });
  };

  // Export — récupère l'ensemble filtré
  const fetchAllForExport = async () => {
    const cleanedQ = q ? q.replace(/\s+/g, '') : '';
    const params = {
      page: 1,
      limit: 5000,
      sort,
      q: cleanedQ || undefined,
      start: dateRange?.[0]?.format('YYYY-MM-DD'),
      end: dateRange?.[1]?.format('YYYY-MM-DD'),
      cityCode: cityFilter || undefined,
    };
    const data = await historiqueNumerosService.historiqueVehiculesModifiesList(params);
    return Array.isArray(data) ? data : (data.items || []);
  };

  const exportExcel = async () => {
    const hideLoading = message.loading('Export Excel en cours...', 0);
    try {
      const all = await fetchAllForExport();
      const rows = all.map(it => ({
        Nom: it.nom || '',
        Prenoms: it.prenoms || '',
        Telephone: it.phone || '',
        Ville: it.ville?.name || '',
        AncienneImmatriculation: it.ancienneImmatriculation || '',
        NouvelleImmatriculation: it.nouvelleImmatriculation || '',
        Date: formatDate(it.date),
      }));
      const ws = XLSX.utils.json_to_sheet(rows);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Vehicules_modifies');
      XLSX.writeFile(wb, 'historique_vehicules_modifies.xlsx');
      message.success(`${all.length} enregistrements exportés`);
    } catch (e) {
      message.error('Export Excel: une erreur est survenue.');
    } finally {
      hideLoading();
    }
  };

  const exportCSV = async () => {
    const hideLoading = message.loading('Export CSV en cours...', 0);
    try {
      const all = await fetchAllForExport();
      const rows = all.map(it => ({
        Nom: it.nom || '',
        Prenoms: it.prenoms || '',
        Telephone: it.phone || '',
        Ville: it.ville?.name || '',
        AncienneImmatriculation: it.ancienneImmatriculation || '',
        NouvelleImmatriculation: it.nouvelleImmatriculation || '',
        Date: formatDate(it.date),
      }));
      const ws = XLSX.utils.json_to_sheet(rows);
      const csv = XLSX.utils.sheet_to_csv(ws);
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      saveAs(blob, 'historique_vehicules_modifies.csv');
      message.success(`${all.length} enregistrements exportés`);
    } catch (e) {
      message.error('Export CSV: une erreur est survenue.');
    } finally {
      hideLoading();
    }
  };

  const exportPDF = async () => {
    const hideLoading = message.loading('Export PDF en cours...', 0);
    try {
      const all = await fetchAllForExport();
      const doc = new jsPDF();
      doc.setFontSize(16);
      doc.text('Historique des changements de véhicules', 14, 15);
      doc.setFontSize(10);
      doc.text(`Généré le ${dayjs().format('DD/MM/YYYY HH:mm')}`, 14, 22);

      autoTable(doc, {
        startY: 30,
        head: [['Nom', 'Prénoms', 'Téléphone', 'Ville', 'Ancienne immat.', 'Nouvelle immat.', 'Date']],
        body: all.map(it => [
          it.nom || '',
          it.prenoms || '',
          it.phone || '',
          it.ville?.name || '',
          it.ancienneImmatriculation || '',
          it.nouvelleImmatriculation || '',
          formatDate(it.date),
        ]),
        styles: { fontSize: 8 },
        headStyles: { fillColor: [114, 46, 209] },
      });
      doc.save('historique_vehicules_modifies.pdf');
      message.success(`${all.length} enregistrements exportés`);
    } catch (e) {
      message.error('Export PDF: une erreur est survenue.');
    } finally {
      hideLoading();
    }
  };

  // Menu dropdown pour export
  const exportMenuItems = [
    {
      key: 'excel',
      icon: <FileExcelOutlined style={{ color: '#52c41a' }} />,
      label: 'Exporter Excel (.xlsx)',
      onClick: exportExcel
    },
    {
      key: 'csv',
      icon: <DownloadOutlined style={{ color: '#1890ff' }} />,
      label: 'Exporter CSV',
      onClick: exportCSV
    },
    {
      key: 'pdf',
      icon: <FilePdfOutlined style={{ color: '#ff4d4f' }} />,
      label: 'Exporter PDF',
      onClick: exportPDF
    }
  ];

  const columns = [
    {
      title: 'Utilisateur',
      key: 'user',
      width: isMobile ? 150 : 220,
      render: (_, record) => {
        const fullName = `${record.nom || ''} ${record.prenoms || ''}`.trim();
        const initials = fullName
          .split(' ')
          .map(n => n[0])
          .join('')
          .toUpperCase()
          .slice(0, 2);
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <Avatar
              size={36}
              style={{
                backgroundColor: getAvatarColor(fullName),
                fontWeight: 'bold',
                fontSize: 14
              }}
            >
              {initials || <UserOutlined />}
            </Avatar>
            <div>
              <div style={{ fontWeight: 500, color: '#262626' }}>
                {fullName || '-'}
              </div>
              <div style={{ fontSize: 12, color: '#8c8c8c' }}>
                {formatPhoneNumber(record.phone)}
              </div>
            </div>
          </div>
        );
      }
    },
    {
      title: 'Ville',
      key: 'ville',
      width: 130,
      render: (_, record) => {
        const nom = record.ville?.name;
        if (!nom) return <span style={{ color: '#bfbfbf' }}>—</span>;
        return (
          <Tag icon={<EnvironmentOutlined />} color="geekblue" style={{ fontSize: 12 }}>
            {nom}
          </Tag>
        );
      },
    },
    {
      title: 'Changement d\'immatriculation',
      key: 'changement',
      width: isMobile ? 200 : 320,
      render: (_, record) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <Tooltip title="Ancienne immatriculation">
            <Tag
              color="red"
              style={{
                margin: 0,
                padding: '4px 12px',
                fontSize: 13,
                fontWeight: 500,
                borderRadius: 6
              }}
            >
              <CarOutlined style={{ marginRight: 6 }} />
              {record.ancienneImmatriculation || '-'}
            </Tag>
          </Tooltip>
          <SwapRightOutlined style={{ color: '#8c8c8c', fontSize: 18 }} />
          <Tooltip title="Nouvelle immatriculation">
            <Tag
              color="green"
              style={{
                margin: 0,
                padding: '4px 12px',
                fontSize: 13,
                fontWeight: 500,
                borderRadius: 6
              }}
            >
              <CarOutlined style={{ marginRight: 6 }} />
              {record.nouvelleImmatriculation || '-'}
            </Tag>
          </Tooltip>
        </div>
      )
    },
    {
      title: 'Date',
      dataIndex: 'date',
      key: 'date',
      width: 160,
      sorter: true,
      defaultSortOrder: 'descend',
      render: (d, row) => (
        <Tooltip title={formatDate(row.date)}>
          <span style={{ color: '#595959' }}>
            <CalendarOutlined style={{ marginRight: 6, color: '#722ed1' }} />
            {row.dateStr || formatDate(d)}
          </span>
        </Tooltip>
      )
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 100,
      align: 'center',
      render: (_, record) => (
        <Can perm="vehicleChanges.history:delete">
          <Tooltip title="Supprimer">
            <Button
              danger
              type="text"
              icon={<DeleteOutlined />}
              size="small"
              onClick={() => handleDelete(record)}
              style={{ borderRadius: 6 }}
            />
          </Tooltip>
        </Can>
      ),
    },
  ];

  const onTableChange = (pagination, _filters, sorter) => {
    setPage(pagination.current || 1);
    setLimit(pagination.pageSize || 10);

    if (sorter?.field || sorter?.columnKey) {
      const key = sorter.columnKey || sorter.field;
      const dir = sorter.order === 'ascend' ? '' : '-';
      if (['date', 'nom', 'prenoms', 'phone', 'ancienneImmatriculation', 'nouvelleImmatriculation'].includes(key)) {
        setSort(`${dir}${key}`);
      }
    }
  };

  // Style des lignes alternées
  const rowClassName = (_, index) => {
    return index % 2 === 0 ? 'table-row-light' : 'table-row-dark';
  };

  return (
    <div className="content-wrapper">
      <style>
        {`
          .table-row-light {
            background-color: #ffffff;
          }
          .table-row-dark {
            background-color: #fafafa;
          }
          .ant-table-tbody > tr:hover > td {
            background-color: #f0f5ff !important;
          }
          .ant-table-thead > tr > th {
            background: #fafafa !important;
            color: #262626 !important;
            font-weight: 600 !important;
          }
          .stat-card-purple {
            background: linear-gradient(135deg, #722ed1 0%, #9254de 100%);
            border-radius: 12px;
            border: none;
          }
          .stat-card-blue {
            background: linear-gradient(135deg, #1890ff 0%, #69c0ff 100%);
            border-radius: 12px;
            border: none;
          }
          .stat-card-green {
            background: linear-gradient(135deg, #52c41a 0%, #95de64 100%);
            border-radius: 12px;
            border: none;
          }
          .stat-card-orange {
            background: linear-gradient(135deg, #fa8c16 0%, #ffc069 100%);
            border-radius: 12px;
            border: none;
          }
          .stat-card-purple .ant-statistic-title,
          .stat-card-blue .ant-statistic-title,
          .stat-card-green .ant-statistic-title,
          .stat-card-orange .ant-statistic-title {
            color: rgba(255,255,255,0.85) !important;
            font-size: 13px !important;
          }
          .stat-card-purple .ant-statistic-content,
          .stat-card-blue .ant-statistic-content,
          .stat-card-green .ant-statistic-content,
          .stat-card-orange .ant-statistic-content {
            color: white !important;
          }
        `}
      </style>

      <div className="container-xxl flex-grow-1 container-p-y" style={{ paddingTop: 0 }}>
        {/* Breadcrumb */}
        <Breadcrumb
          separator={<RightOutlined style={{ fontSize: 12, color: '#8c8c8c' }} />}
          style={{ marginBottom: 12, marginTop: 0 }}
        >
          <Breadcrumb.Item>
            <Link to="/dashboard" style={{ color: '#722ed1' }}>Accueil</Link>
          </Breadcrumb.Item>
          <Breadcrumb.Item>
            <Link to="/dashboard" style={{ color: '#722ed1' }}>Historique</Link>
          </Breadcrumb.Item>
          <Breadcrumb.Item>
            <span style={{ color: '#595959' }}>Modification Véhicules</span>
          </Breadcrumb.Item>
        </Breadcrumb>

        {/* Statistiques */}
        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          <Col xs={12} sm={12} md={6}>
            <Card className="stat-card-purple" bodyStyle={{ padding: isMobile ? 12 : 16 }}>
              <Statistic
                title={<span><HistoryOutlined style={{ marginRight: 6 }} />Total changements</span>}
                value={stats.totalChangements}
                valueStyle={{ fontSize: isMobile ? 20 : 28, fontWeight: 'bold' }}
              />
            </Card>
          </Col>
          <Col xs={12} sm={12} md={6}>
            <Card className="stat-card-blue" bodyStyle={{ padding: isMobile ? 12 : 16 }}>
              <Statistic
                title={<span><UserOutlined style={{ marginRight: 6 }} />Utilisateurs uniques</span>}
                value={stats.utilisateursUniques}
                valueStyle={{ fontSize: isMobile ? 20 : 28, fontWeight: 'bold' }}
              />
            </Card>
          </Col>
          <Col xs={12} sm={12} md={6}>
            <Card className="stat-card-green" bodyStyle={{ padding: isMobile ? 12 : 16 }}>
              <Statistic
                title={<span><CalendarOutlined style={{ marginRight: 6 }} />Aujourd'hui</span>}
                value={stats.today}
                valueStyle={{ fontSize: isMobile ? 20 : 28, fontWeight: 'bold' }}
              />
            </Card>
          </Col>
          <Col xs={12} sm={12} md={6}>
            <Card className="stat-card-orange" bodyStyle={{ padding: isMobile ? 12 : 16 }}>
              <Statistic
                title={<span><BarChartOutlined style={{ marginRight: 6 }} />Ce mois</span>}
                value={stats.thisMonth}
                valueStyle={{ fontSize: isMobile ? 20 : 28, fontWeight: 'bold' }}
              />
            </Card>
          </Col>
        </Row>

        {/* Carte principale */}
        <Card
          style={{
            borderRadius: 16,
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
          }}
          bodyStyle={{ padding: isMobile ? 12 : 24 }}
        >
          {/* Filtres */}
          <Row gutter={[12, 12]} style={{ marginBottom: 20 }}>
            <Col xs={24} sm={24} md={7}>
              <Input
                prefix={<SearchOutlined style={{ color: '#722ed1' }} />}
                placeholder="Rechercher par nom, téléphone, immatriculation..."
                value={q}
                onChange={(e) => { setQ(e.target.value); setPage(1); }}
                allowClear
                size={controlSize}
                style={{ borderRadius: 8 }}
              />
            </Col>
            <Col xs={24} sm={12} md={6}>
              <RangePicker
                onChange={(range) => { setDateRange(range); setPage(1); }}
                placeholder={['Date début', 'Date fin']}
                format="DD/MM/YYYY"
                allowClear
                size={controlSize}
                style={{ width: '100%', borderRadius: 8 }}
              />
            </Col>
            <Col xs={24} sm={12} md={5}>
              <Select
                allowClear
                showSearch
                placeholder={
                  <span>
                    <EnvironmentOutlined style={{ marginRight: 6 }} />
                    Filtrer par ville
                  </span>
                }
                value={cityFilter}
                onChange={(v) => { setCityFilter(v); setPage(1); }}
                size={controlSize}
                style={{ width: '100%' }}
                optionFilterProp="label"
                options={cities.map(c => ({ value: c.code, label: c.name }))}
              />
            </Col>
            <Col xs={24} sm={12} md={6} style={{ display: 'flex', justifyContent: isMobile ? 'flex-start' : 'flex-end' }}>
              <Dropdown
                menu={{ items: exportMenuItems }}
                placement="bottomRight"
                trigger={['click']}
              >
                <Button
                  type="primary"
                  icon={<DownloadOutlined />}
                  size={controlSize}
                  style={{
                    borderRadius: 8,
                    background: 'linear-gradient(135deg, #722ed1 0%, #9254de 100%)',
                    border: 'none'
                  }}
                >
                  Exporter {!isMobile && `(${total})`}
                </Button>
              </Dropdown>
            </Col>
          </Row>

          {/* Table */}
          <Table
            rowKey="_id"
            columns={columns}
            dataSource={items}
            loading={isLoading}
            onChange={onTableChange}
            size={controlSize}
            scroll={{ x: 800 }}
            rowClassName={rowClassName}
            pagination={{
              current: page,
              pageSize: limit,
              total,
              showSizeChanger: true,
              pageSizeOptions: [5, 10, 20, 50, 100],
              showTotal: (t, range) => (
                <span style={{ color: '#8c8c8c' }}>
                  {range[0]}-{range[1]} sur {t} enregistrements
                </span>
              ),
              showLessItems: isMobile,
            }}
          />
        </Card>
      </div>
    </div>
  );
};

export default HistoriqueVehiculesModifies;
